import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

interface AffiliateProgram {
  id: string;
  name: string;
  description: string;
  commissionType: 'percentage' | 'fixed';
  commissionValue: number;
  cookieDuration: number;
  minimumPayout: number;
  paymentTerms: string;
  isActive: boolean;
  isDefault?: boolean;
  productCategories: string[];
  createdAt: string;
  updatedAt: string;
}

// Sample data for the placeholder
const samplePrograms: AffiliateProgram[] = [
  {
    id: 'prog-1',
    name: 'Standard Affiliate Program',
    description: 'Our standard affiliate program for all products and services.',
    commissionType: 'percentage',
    commissionValue: 10,
    cookieDuration: 30,
    minimumPayout: 50,
    paymentTerms: 'Monthly',
    isActive: true,
    isDefault: true,
    productCategories: ['All Products'],
    createdAt: '2025-05-01T08:30:00Z',
    updatedAt: '2025-06-10T14:20:00Z'
  },
  {
    id: 'prog-2',
    name: 'Premium Partners',
    description: 'Exclusive program for high-performing affiliates with higher commissions.',
    commissionType: 'percentage',
    commissionValue: 15,
    cookieDuration: 60,
    minimumPayout: 100,
    paymentTerms: 'Monthly',
    isActive: true,
    productCategories: ['Premium Plans', 'Annual Subscriptions'],
    createdAt: '2025-05-15T09:45:00Z',
    updatedAt: '2025-06-20T11:30:00Z'
  },
  {
    id: 'prog-3',
    name: 'Summer Promotion',
    description: 'Special limited-time program for summer marketing campaign.',
    commissionType: 'fixed',
    commissionValue: 25,
    cookieDuration: 45,
    minimumPayout: 50,
    paymentTerms: 'Bi-weekly',
    isActive: true,
    productCategories: ['Summer Bundle'],
    createdAt: '2025-06-01T10:00:00Z',
    updatedAt: '2025-07-05T16:15:00Z'
  }
];

export const ProgramsManagement: React.FC = () => {
  const [programs, setPrograms] = useState<AffiliateProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<AffiliateProgram | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Form state for new/edit program
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    commissionType: 'percentage' as 'percentage' | 'fixed',
    commissionValue: 10,
    cookieDuration: 30,
    minimumPayout: 50,
    paymentTerms: 'Monthly',
    isActive: true,
    productCategories: ['All Products']
  });
  
  // Fetch programs - to be implemented with real API
  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setPrograms(samplePrograms);
      setLoading(false);
    }, 500);
  }, []);
  
  // Filter programs based on search and status
  const filteredPrograms = programs.filter(program => {
    const matchesSearch = 
      program.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'active' && program.isActive) || 
      (statusFilter === 'inactive' && !program.isActive);
    
    return matchesSearch && matchesStatus;
  });
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  // Handle numeric input changes
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) }));
  };
  
  // Open edit modal
  const handleEdit = (program: AffiliateProgram) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      description: program.description,
      commissionType: program.commissionType,
      commissionValue: program.commissionValue,
      cookieDuration: program.cookieDuration,
      minimumPayout: program.minimumPayout,
      paymentTerms: program.paymentTerms,
      isActive: program.isActive,
      productCategories: program.productCategories
    });
    setShowCreateModal(true);
  };
  
  // Open create modal
  const handleCreate = () => {
    setEditingProgram(null);
    setFormData({
      name: '',
      description: '',
      commissionType: 'percentage',
      commissionValue: 10,
      cookieDuration: 30,
      minimumPayout: 50,
      paymentTerms: 'Monthly',
      isActive: true,
      productCategories: ['All Products']
    });
    setShowCreateModal(true);
  };
  
  // Toggle program status
  const toggleProgramStatus = (id: string) => {
    setPrograms(prevPrograms => 
      prevPrograms.map(program => 
        program.id === id 
          ? { ...program, isActive: !program.isActive } 
          : program
      )
    );
    toast.success('Program status updated');
  };
  
  // Set program as default
  const setAsDefaultProgram = (id: string) => {
    setPrograms(prevPrograms => 
      prevPrograms.map(program => ({
        ...program,
        isDefault: program.id === id
      }))
    );
    toast.success('Default program updated');
  };
  
  // Save program (create or edit)
  const handleSaveProgram = () => {
    if (!formData.name.trim()) {
      toast.error('Program name is required');
      return;
    }
    
    if (editingProgram) {
      // Update existing program
      setPrograms(prevPrograms => 
        prevPrograms.map(program => 
          program.id === editingProgram.id 
            ? { 
                ...program, 
                ...formData,
                updatedAt: new Date().toISOString()
              } 
            : program
        )
      );
      toast.success('Program updated successfully');
    } else {
      // Create new program
      const newProgram: AffiliateProgram = {
        ...formData,
        id: `prog-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setPrograms([...programs, newProgram]);
      toast.success('Program created successfully');
    }
    
    setShowCreateModal(false);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Affiliate Programs</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Create and manage multiple affiliate programs with different terms and conditions.
          </p>
        </div>
        
        {/* Filters and Actions */}
        <div className="px-4 py-5 sm:px-6 flex flex-wrap gap-4 items-center justify-between bg-gray-50 border-t border-b border-gray-200">
          {/* Search */}
          <div className="w-full md:w-auto">
            <label htmlFor="search" className="sr-only">Search</label>
            <input
              type="text"
              id="search"
              name="search"
              placeholder="Search programs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          
          {/* Filters */}
          <div className="flex items-center">
            <label htmlFor="status-filter" className="mr-2 text-sm font-medium text-gray-700">Status:</label>
            <select
              id="status-filter"
              name="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          {/* Create Button */}
          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create New Program
          </button>
        </div>
        
        {/* Programs List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Program</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cookie Duration</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minimum Payout</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrograms.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    No programs found
                  </td>
                </tr>
              ) : (
                filteredPrograms.map((program) => (
                  <tr key={program.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{program.name}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{program.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {program.commissionType === 'percentage' 
                        ? `${program.commissionValue}%` 
                        : `£${program.commissionValue.toFixed(2)}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {program.cookieDuration} days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      £{program.minimumPayout}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        program.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {program.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {program.isDefault && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(program)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Edit
                      </button>
                      {!program.isDefault && (
                        <button
                          onClick={() => setAsDefaultProgram(program.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => toggleProgramStatus(program.id)}
                        className={program.isActive ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                      >
                        {program.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {editingProgram ? 'Edit Affiliate Program' : 'Create Affiliate Program'}
                </h3>
                
                <div className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Program Name *</label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="commissionType" className="block text-sm font-medium text-gray-700">Commission Type</label>
                      <select
                        id="commissionType"
                        name="commissionType"
                        value={formData.commissionType}
                        onChange={handleInputChange}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="commissionValue" className="block text-sm font-medium text-gray-700">
                        {formData.commissionType === 'percentage' ? 'Commission (%)' : 'Commission Amount (£)'}
                      </label>
                      <input
                        type="number"
                        name="commissionValue"
                        id="commissionValue"
                        min="0"
                        step={formData.commissionType === 'percentage' ? "0.01" : "1"}
                        value={formData.commissionValue}
                        onChange={handleNumericChange}
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cookieDuration" className="block text-sm font-medium text-gray-700">Cookie Duration (days)</label>
                      <input
                        type="number"
                        name="cookieDuration"
                        id="cookieDuration"
                        min="1"
                        value={formData.cookieDuration}
                        onChange={handleNumericChange}
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="minimumPayout" className="block text-sm font-medium text-gray-700">Minimum Payout (£)</label>
                      <input
                        type="number"
                        name="minimumPayout"
                        id="minimumPayout"
                        min="0"
                        value={formData.minimumPayout}
                        onChange={handleNumericChange}
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700">Payment Terms</label>
                    <select
                      id="paymentTerms"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Bi-weekly">Bi-weekly</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Quarterly">Quarterly</option>
                    </select>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="isActive"
                        name="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={handleCheckboxChange}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="isActive" className="font-medium text-gray-700">Active</label>
                      <p className="text-gray-500">Program is active and available to affiliates</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSaveProgram}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {editingProgram ? 'Save Changes' : 'Create Program'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramsManagement;
